import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import ListEmpty from '@/containers/common/error/ListEmpty';
import useInfiniteQueryHook from '@/hooks/useInfiniteQueryHook';
import useIntersect from '@/hooks/useIntersect';
import QnAInfoByStatus from '@/components/ContextPrinter/QnAInfoByStatus';
import { questionEnrollState } from '@/recoil/ina/myQuestion';
import { API, URLs } from '@/api';
import { QuestionList } from '@/models/ina';

interface IProps {
    index: number;
    questionStatus?: number;
    saveScroll: () => void;
    setIsLoadDataMore: () => void;
}

const MyQuestionListItem = ({ questionStatus, saveScroll, setIsLoadDataMore }: IProps) => {
    const router = useRouter();
    const isQuestionEnrolled = useRecoilValue(questionEnrollState);
    const [isLoading, setIsLoading] = useState(true);

    const onClickQuestion = async (questionId: number) => {
        saveScroll();

        router.push(
            {
                pathname: `/ina/my/${questionId}`,
                query: {
                    isSearching: false,
                    myQuestion: true,
                },
            },
            `/ina/my/${questionId}`,
        );
    };

    const {
        data: questionList,
        isSuccess,
        RenderError,
        isError,
        refetch,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQueryHook(['qnaMyQuestionList', questionStatus!], async ({ pageParam = 1 }) => {
        return await API.get(URLs.ina.myQuestionList, { questionStatus, page: pageParam });
    });

    const handleIntersect = useIntersect(async () => {
        if (questionList && questionList.length === 0) {
            setIsLoading(false);
            return;
        }

        if (hasNextPage) {
            await fetchNextPage();
            setIsLoading(true);
            setTimeout(() => {
                setIsLoadDataMore();
            }, 800);
        } else {
            setIsLoading(false);
        }
    });

    const renderItem = () => {
        if (questionList.length === 0) return <ListEmpty text={'아직 등록된 질문이 없어요'} />;

        return questionList.map((item: QuestionList, index: number) => (
            <div key={`${item.id}_${index}`} onClick={() => onClickQuestion(item.id)}>
                <QnAInfoByStatus
                    questionId={item.id}
                    status={item.status}
                    content={item.content}
                    answerCount={item.answerCount}
                    showStatus={true}
                    contentLine={2}
                    // style={{ padding: '18px 30px' }}
                    hasDivider
                    isLast={questionList.length - 1 === index}
                    contentTitle={item.title}
                />
            </div>
        ));
    };

    useEffect(() => {
        refetch();
    }, [isQuestionEnrolled]);

    return (
        <Container>
            {isSuccess && renderItem()}
            {isError && <ErrorContainer>{RenderError}</ErrorContainer>}
            {isLoading && (
                <LoadingWrapper>
                    <Image src={`/img/common/loading_grey.gif`} width={50} height={50} alt="loading" />
                </LoadingWrapper>
            )}
            <TargetOfInfiniteScroll ref={handleIntersect} />
        </Container>
    );
};

const Container = styled.div``;

const ErrorContainer = styled.div`
    padding-top: 50px;
    padding-bottom: 100px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const TargetOfInfiniteScroll = styled.div`
    height: 1px;
`;

export default MyQuestionListItem;
